import React, { useEffect, Suspense, lazy, memo, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { createRoot } from 'react-dom/client';

import './index.css';
import './utils/webVitals';
import { initializeEnvValidation } from './utils/envValidation';
import { usePerformanceMonitor } from './hooks/usePerformanceMonitor';
import { ROUTES } from './domain/routes';
import { TIMEVALUES } from './domain/constants';
import { CartProvider } from './state/CartContext';
import { ThemeProvider } from './state/ThemeContext';
import { AuthProvider, useAuth } from './state/auth';
import { ToastProvider } from './state/ToastContext';
import { FavoritesProvider } from './state/FavoritesContext';
import { PartnerProvider } from './state/PartnerContext';
import { MarketingProvider, useMarketing } from './state/MarketingContext';
import { ContentProvider } from './state/ContentContext';
import { ClubProvider } from './state/ClubContext';
import { SocialProofProvider } from './state/SocialProofContext';
import { NotificationProvider } from './state/NotificationContext';
import { AudioProvider } from './state/AudioContext';
import { GamificationProvider } from './state/GamificationContext';
import { WalletProvider } from './state/WalletContext';
import { AddressProvider } from './state/AddressContext';
import { CoreProviders } from './state/CoreProviders';
import { DataProviders } from './state/DataProviders';
import { UIProviders } from './state/UIProviders';
import { AppProviders } from './state/AppProviders';
import { MainLayout } from './layouts/MainLayout';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { PerformanceReport } from './components/PerformanceReport';
import { SmartPrefetcher } from './components/SmartPrefetcher';
import { AppRole, ADMIN_ROLES, PARTNER_ROLES } from './domain/rbac';

// EAGER LOAD critical screens (Home & Cart) for instant navigation
import { HomeScreen } from './features/products/HomeScreen';
import { CartScreen } from './features/cart/CartScreen';
import { CatalogScreen } from './features/products/CatalogScreen';
import { ProductDetailScreen } from './features/products/ProductDetailScreen';

// Lazy load non-critical screens
const LoginScreen = lazy(() => import('./features/auth/LoginScreen').then(m => ({ default: m.LoginScreen })));
const ForgotPasswordScreen = lazy(() => import('./features/auth/ForgotPasswordScreen').then(m => ({ default: m.ForgotPasswordScreen })));
const RegisterSuccessScreen = lazy(() => import('./features/auth/RegisterSuccessScreen').then(m => ({ default: m.RegisterSuccessScreen })));
const BenefitsScreen = lazy(() => import('./features/auth/BenefitsScreen').then(m => ({ default: m.BenefitsScreen })));
const OnboardingScreen = lazy(() => import('./features/intro/OnboardingScreen').then(m => ({ default: m.OnboardingScreen })));
const ShippingScreen = lazy(() => import('./features/cart/ShippingScreen').then(m => ({ default: m.ShippingScreen })));
const PaymentScreen = lazy(() => import('./features/cart/PaymentScreen').then(m => ({ default: m.PaymentScreen })));
const CheckoutReviewScreen = lazy(() => import('./features/cart/CheckoutReviewScreen').then(m => ({ default: m.CheckoutReviewScreen })));
const SuccessScreen = lazy(() => import('./features/cart/SuccessScreen').then(m => ({ default: m.SuccessScreen })));
const TrackingScreen = lazy(() => import('./features/orders/TrackingScreen').then(m => ({ default: m.TrackingScreen })));
const OrdersHistoryScreen = lazy(() => import('./features/orders/OrdersHistoryScreen').then(m => ({ default: m.OrdersHistoryScreen })));
const OrderDetailScreen = lazy(() => import('./features/orders/OrderDetailScreen').then(m => ({ default: m.OrderDetailScreen })));
const ProfileScreen = lazy(() => import('./features/profile/ProfileScreen').then(m => ({ default: m.ProfileScreen })));
const AddressScreen = lazy(() => import('./features/profile/AddressScreen').then(m => ({ default: m.AddressScreen })));
const WalletScreen = lazy(() => import('./features/wallet/WalletScreen').then(m => ({ default: m.WalletScreen })));
const TopUpScreen = lazy(() => import('./features/wallet/TopUpScreen').then(m => ({ default: m.TopUpScreen })));
const TopUpReviewScreen = lazy(() => import('./features/wallet/TopUpReviewScreen').then(m => ({ default: m.TopUpReviewScreen })));
const WalletHistoryScreen = lazy(() => import('./features/wallet/WalletHistoryScreen').then(m => ({ default: m.WalletHistoryScreen })));
const PartnerRegistrationScreen = lazy(() => import('./features/partner/PartnerRegistrationScreen').then(m => ({ default: m.PartnerRegistrationScreen })));
const PartnerDashboardScreen = lazy(() => import('./features/partner/PartnerDashboardScreen').then(m => ({ default: m.PartnerDashboardScreen })));
const ProductCreationScreen = lazy(() => import('./features/partner/ProductCreationScreen').then(m => ({ default: m.ProductCreationScreen })));
const PartnerServicesScreen = lazy(() => import('./features/partner/PartnerServicesScreen').then(m => ({ default: m.PartnerServicesScreen })));
const PartnerFinanceScreen = lazy(() => import('./features/partner/PartnerFinanceScreen').then(m => ({ default: m.PartnerFinanceScreen })));
const PartnerProductsScreen = lazy(() => import('./features/partner/PartnerProductsScreen').then(m => ({ default: m.PartnerProductsScreen })));
const PartnerReviewsScreen = lazy(() => import('./features/partner/PartnerReviewsScreen').then(m => ({ default: m.PartnerReviewsScreen })));
const ProductReviewsScreen = lazy(() => import('./features/products/ProductReviewsScreen').then(m => ({ default: m.ProductReviewsScreen })));
const ShopPublicScreen = lazy(() => import('./features/shop/ShopPublicScreen').then(m => ({ default: m.ShopPublicScreen })));
const ShopsDirectoryScreen = lazy(() => import('./features/shop/ShopsDirectoryScreen').then(m => ({ default: m.ShopsDirectoryScreen })));
const WishlistScreen = lazy(() => import('./features/user/WishlistScreen').then(m => ({ default: m.WishlistScreen })));
const ChatScreen = lazy(() => import('./features/messages/ChatScreen').then(m => ({ default: m.ChatScreen })));
const ChatDetailScreen = lazy(() => import('./features/messages/ChatDetailScreen').then(m => ({ default: m.ChatDetailScreen })));
const AdminScreen = lazy(() => import('./features/admin/AdminScreen').then(module => ({ default: module.AdminScreen })));
const AdminOperationsScreen = lazy(() => import('./features/admin/AdminOperationsScreen').then(module => ({ default: module.AdminOperationsScreen })));
const ClubScreen = lazy(() => import('./features/club/ClubScreen').then(m => ({ default: m.ClubScreen })));
const TucCoinsScreen = lazy(() => import('./features/legal/TucCoinsScreen').then(m => ({ default: m.TucCoinsScreen })));
const LegalScreen = lazy(() => import('./features/legal/LegalScreen').then(m => ({ default: m.LegalScreen })));
const FAQScreen = lazy(() => import('./features/support/FAQScreen').then(m => ({ default: m.FAQScreen })));
const BlogScreen = lazy(() => import('./features/blog/BlogScreen').then(m => ({ default: m.BlogScreen })));
const MembershipPlansScreen = lazy(() => import('./features/membership/MembershipPlansScreen').then(m => ({ default: m.MembershipPlansScreen })));
const NotFoundScreen = lazy(() => import('./features/NotFoundScreen').then(m => ({ default: m.NotFoundScreen })));

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  partnerOnly?: boolean;
  requiredRole?: AppRole;
  requiredAnyRole?: AppRole[];
}

interface GuestRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const LoadingScreen: React.FC<{ message?: string }> = ({ message = 'Cargando...' }) => (
  <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false,
  partnerOnly = false,
  requiredRole,
  requiredAnyRole
}) => {
  const { user, isInitialLoading, canAccessAdmin, isPartner, userRole, isAdmin } = useAuth();
  const location = useLocation();

  if (isInitialLoading) {
    return <LoadingScreen message="Verificando sesión..." />;
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location.pathname }} replace />;
  }

  if (adminOnly && !canAccessAdmin) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (partnerOnly && !isPartner && userRole !== 'superadmin') {
    return <Navigate to={ROUTES.PARTNER.REGISTRATION} replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (requiredAnyRole && !requiredAnyRole.includes(userRole)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

const GuestRoute: React.FC<GuestRouteProps> = ({ 
  children, 
  redirectTo = ROUTES.HOME 
}) => {
  const { user, isInitialLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const from = (location.state as { from?: Location } | undefined)?.from || redirectTo;

  React.useEffect(() => {
    if (!isInitialLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, isInitialLoading, navigate, from]);

  if (isInitialLoading) {
    return <LoadingScreen message="Verificando sesión..." />;
  }

  if (user) {
    return null;
  }

  return <>{children}</>;
};

// --- MAIN APPLICATION CONTENT ---
const AppContent = () => {
    const { seoConfig, isLoading: isMarketingLoading } = useMarketing();
    const { isInitialLoading: isAuthLoading } = useAuth();
    const { reportMetrics } = usePerformanceMonitor();
    const navigate = useNavigate();
    const location = useLocation();

    const [showSplash, setShowSplash] = React.useState(true);
    const [fadeOut, setFadeOut] = React.useState(false);

    useEffect(() => {
        // Report performance metrics when app is fully loaded
        if (!isAuthLoading && !isMarketingLoading) {
            reportMetrics();
        }
    }, [isAuthLoading, isMarketingLoading, reportMetrics]);

useEffect(() => {
    let ticking = false;
    const updateVh = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty('--vh', `${vh}px`);
          ticking = false;
        });
        ticking = true;
      }
    };
    updateVh();
    window.addEventListener('resize', updateVh);
    return () => window.removeEventListener('resize', updateVh);
  }, []);

    useEffect(() => {
        document.title = seoConfig.siteTitle;
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', seoConfig.siteDescription);
    }, [seoConfig]);

    useEffect(() => {
        const hasSeenOnboarding = localStorage.getItem('alltuc_onboarding_seen');
        const isPublicRoute = ['/login', '/onboarding', '/register-success'].includes(location.pathname);
        const isReturningFromPayment = location.search.includes('status=') || location.search.includes('orderId=');
        const isGoogleRedirect = location.search.includes('apiKey=') || location.search.includes('oobCode=');

        if (!hasSeenOnboarding && !isPublicRoute && !isAuthLoading && !isReturningFromPayment && !isGoogleRedirect) {
            navigate('/onboarding', { replace: true });
        }
    }, [location.pathname, location.search, navigate, isAuthLoading]);

  useEffect(() => {
    if (!isMarketingLoading && !isAuthLoading) {
      const timer = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => setShowSplash(false), TIMEVALUES.SPLASH_HIDE_DELAY_MS);
      }, TIMEVALUES.SPLASH_FADE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isMarketingLoading, isAuthLoading]);

    const PageLoader = () => (
        <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    const isOnboarding = location.pathname === ROUTES.ONBOARDING;

    return (
        <>
            {showSplash && !isOnboarding && (
                <div className={`fixed inset-0 z-[1000] bg-background-light dark:bg-background-dark flex flex-col items-center justify-center transition-all duration-500 ease-in-out ${fadeOut ? 'opacity-0 scale-110 pointer-events-none' : 'opacity-100'}`}>
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute -top-[10%] -right-[10%] w-[60%] h-[40%] bg-primary/10 rounded-full blur-[120px] animate-pulse"></div>
                        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-t from-primary/5 via-transparent to-transparent"></div>
                    </div>
                    <div className="relative">
                        <div className="absolute inset-0 bg-primary/30 blur-[80px] rounded-full scale-150 animate-float opacity-50"></div>
                        <div className="relative z-10 flex flex-col items-center">
                            <h2 className="text-5xl font-black text-text-main dark:text-white tracking-tighter leading-none">AllTuc</h2>
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary mt-1">Marketplace</span>
                            <div className="w-16 h-1 bg-black/5 dark:bg-white/10 rounded-full overflow-hidden relative mt-10">
                                <div className="h-full bg-primary animate-progress relative z-10" style={{ animationDuration: '1.2s' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {(!showSplash || fadeOut) && (
                <Routes>
                    <Route path={ROUTES.ONBOARDING} element={<Suspense fallback={<PageLoader />}><OnboardingScreen /></Suspense>} />
                    <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.HOME} replace />} />
                    
                    {/* Auth routes - only for guests */}
                    <Route path={ROUTES.LOGIN} element={
                        <GuestRoute redirectTo={ROUTES.HOME}>
                            <Suspense fallback={<PageLoader />}><LoginScreen /></Suspense>
                        </GuestRoute>
                    } />
                    <Route path={ROUTES.REGISTER_SUCCESS} element={
                        <GuestRoute>
                            <Suspense fallback={<PageLoader />}><RegisterSuccessScreen /></Suspense>
                        </GuestRoute>
                    } />
          <Route path={ROUTES.BENEFITS} element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}><BenefitsScreen /></Suspense>
            </GuestRoute>
          } />
          <Route path={ROUTES.FORGOT_PASSWORD} element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}><ForgotPasswordScreen /></Suspense>
            </GuestRoute>
          } />

        <Route element={<MainLayout />}>
          {/* EAGER LOADED - No Suspense wrapper for instant navigation */}
          <Route path={ROUTES.HOME} element={<HomeScreen />} />
          <Route path={ROUTES.CATALOG} element={<CatalogScreen />} />
          <Route path="/favorites" element={<CatalogScreen />} />
          
          {/* Lazy loaded routes with Suspense */}
          <Route path={ROUTES.WISHLIST} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WishlistScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.TRACKING} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TrackingScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PROFILE} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ProfileScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.ADDRESSES} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AddressScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.CLUB} element={<Suspense fallback={<PageLoader />}><ClubScreen /></Suspense>} />
                        
                        {/* Partner Routes */}
                        <Route path={ROUTES.PARTNER.REGISTRATION} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PartnerRegistrationScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.DASHBOARD} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><PartnerDashboardScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.NEW_PRODUCT} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><ProductCreationScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.PRODUCTS} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><PartnerProductsScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.SERVICES} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><PartnerServicesScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.REVIEWS} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><PartnerReviewsScreen /></Suspense></ProtectedRoute>} />
                        <Route path={ROUTES.PARTNER.FINANCE} element={<ProtectedRoute partnerOnly={true}><Suspense fallback={<PageLoader />}><PartnerFinanceScreen /></Suspense></ProtectedRoute>} />
                        
{/* User Routes */}
<Route path={ROUTES.ORDERS_HISTORY} element={<Suspense fallback={<PageLoader />}><OrdersHistoryScreen /></Suspense>} />
          <Route path={ROUTES.ORDER_DETAIL} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><OrderDetailScreen /></Suspense></ProtectedRoute>} />
          <Route path={ROUTES.CHATS} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ChatScreen /></Suspense></ProtectedRoute>} />
          <Route path={ROUTES.CHAT_DETAIL.replace(':chatId', ':chatId')} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ChatDetailScreen /></Suspense></ProtectedRoute>} />
          <Route path={ROUTES.WALLET} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WalletScreen /></Suspense></ProtectedRoute>} />
          <Route path={ROUTES.WALLET_TOPUP} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TopUpScreen /></Suspense></ProtectedRoute>} />
<Route path={ROUTES.WALLET_TOPUP_REVIEW} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TopUpReviewScreen /></Suspense></ProtectedRoute>} />
<Route path={ROUTES.WALLET_HISTORY} element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WalletHistoryScreen /></Suspense></ProtectedRoute>} />
                        
                        {/* Content Routes */}
                        <Route path={ROUTES.BLOG} element={<Suspense fallback={<PageLoader />}><BlogScreen /></Suspense>} />
                        <Route path="/blog/:id" element={<Suspense fallback={<PageLoader />}><BlogScreen /></Suspense>} />
                        <Route path={ROUTES.FAQ} element={<Suspense fallback={<PageLoader />}><FAQScreen /></Suspense>} />
                        <Route path={ROUTES.MEMBERSHIP_PLANS} element={<Suspense fallback={<PageLoader />}><MembershipPlansScreen /></Suspense>} />
                        <Route path={ROUTES.TUCCOINS_INFO} element={<Suspense fallback={<PageLoader />}><TucCoinsScreen /></Suspense>} />
                        <Route path="/legal/:type" element={<Suspense fallback={<PageLoader />}><LegalScreen /></Suspense>} />

{/* Shop Routes */}
            <Route path={ROUTES.SHOPS_DIRECTORY} element={<Suspense fallback={<PageLoader />}><ShopsDirectoryScreen /></Suspense>} />
            <Route path={ROUTES.SHOP_PUBLIC.replace(':slug', ':slug')} element={<Suspense fallback={<PageLoader />}><ShopPublicScreen /></Suspense>} />
          </Route>

        <Route path={ROUTES.PRODUCT_DETAIL.replace(':id', ':id')} element={<ProductDetailScreen />} />
        <Route path={ROUTES.PRODUCT_REVIEWS.replace(':id', ':id')} element={<Suspense fallback={<PageLoader />}><ProductReviewsScreen /></Suspense>} />
        {/* EAGER LOADED - No Suspense wrapper for instant navigation */}
        <Route path={ROUTES.CART} element={<CartScreen />} />
        <Route path={ROUTES.SHIPPING} element={<Suspense fallback={<PageLoader />}><ShippingScreen /></Suspense>} />
                    <Route path={ROUTES.PAYMENT} element={<Suspense fallback={<PageLoader />}><PaymentScreen /></Suspense>} />
                    <Route path={ROUTES.CHECKOUT_REVIEW} element={<Suspense fallback={<PageLoader />}><CheckoutReviewScreen /></Suspense>} />
                    <Route path={ROUTES.SUCCESS} element={<Suspense fallback={<PageLoader />}><SuccessScreen /></Suspense>} />

                    <Route path={ROUTES.ADMIN.BASE} element={<ProtectedRoute adminOnly={true}><Suspense fallback={<PageLoader />}><AdminScreen /></Suspense></ProtectedRoute>} />
                    <Route path={ROUTES.ADMIN.OPERATIONS} element={<ProtectedRoute adminOnly={true}><Suspense fallback={<PageLoader />}><AdminOperationsScreen /></Suspense></ProtectedRoute>} />

                    <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundScreen /></Suspense>} />
                </Routes>
            )}
        </>
    );
}

const App: React.FC = () => {
  return (
    <GlobalErrorBoundary>
        <BrowserRouter>
            <AppProviders>
                <AppContent />
                <SmartPrefetcher />
                <PerformanceReport />
            </AppProviders>
        </BrowserRouter>
    </GlobalErrorBoundary>
  );
};

const root = createRoot(document.getElementById('root')!);
root.render(<React.StrictMode><App /></React.StrictMode>);